import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useNavigate, Link } from 'react-router-dom';
import { client, getMyListings } from '../api/client';
import { Property, Auction } from '../types';
import { useI18n } from '../i18n';
import { Pagination } from '../components/common/Pagination';
import { useToast } from '../components/common/Toast';

interface EditModalProps {
  listing: Property | Auction;
  onClose: () => void;
  onSave: (updates: Partial<Property>) => void;
  language: 'sr' | 'en';
}

function EditModal({ listing, onClose, onSave, language }: EditModalProps) {
  const [title, setTitle] = useState(listing.title);
  const [description, setDescription] = useState(listing.description);
  const [price, setPrice] = useState(listing.price.toString());
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    onSave({
      title,
      description,
      price: parseFloat(price)
    });
    setSaving(false);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-lg w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {language === 'sr' ? 'Izmeni oglas' : 'Edit Listing'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full">
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {language === 'sr' ? 'Naslov' : 'Title'}
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {language === 'sr' ? 'Opis' : 'Description'}
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {language === 'sr' ? 'Cena' : 'Price'} ({listing.currency})
            </label>
            <input
              type="number"
              min="0"
              value={price}
              onChange={e => setPrice(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
            >
              {saving 
                ? (language === 'sr' ? 'Čuvanje...' : 'Saving...') 
                : (language === 'sr' ? 'Sačuvaj' : 'Save')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

interface ReactivateModalProps {
  listing: Property | Auction;
  onClose: () => void;
  onConfirm: (days: number) => void;
  tokenBalance: number;
  language: 'sr' | 'en';
}

function ReactivateModal({ listing, onClose, onConfirm, tokenBalance, language }: ReactivateModalProps) {
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [processing, setProcessing] = useState(false);
  const [durationOptions, setDurationOptions] = useState<{days: number; tokens: number; label: string}[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Fetch duration options from API based on listing type
  useEffect(() => {
    const loadDurationOptions = async () => {
      setLoadingOptions(true);
      try {
        const adKind = listing.adKind || 'Ponuda';
        const res = await client.api.fetch(`/api/pricing/listing-durations?ad_kind=${encodeURIComponent(adKind)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.data && Array.isArray(data.data)) {
            const options = data.data.map((d: any) => ({
              days: Number(d.days),
              tokens: Number(d.tokens),
              label: d.label || `${d.days} ${language === 'sr' ? 'dana' : 'days'}`
            }));
            setDurationOptions(options);
            // Set default to first available option
            if (options.length > 0) {
              setSelectedDuration(options[0].days);
            }
          }
        } else {
          throw new Error('API error');
        }
      } catch (err) {
        console.error('Failed to load duration options:', err);
        // Fallback based on listing type
        const isAuction = listing.adKind === 'Aukcija';
        const fallback = isAuction 
          ? [
              { days: 7, tokens: 2, label: language === 'sr' ? '7 dana' : '7 days' },
              { days: 15, tokens: 3, label: language === 'sr' ? '15 dana' : '15 days' },
              { days: 30, tokens: 5, label: language === 'sr' ? '30 dana' : '30 days' },
              { days: 60, tokens: 8, label: language === 'sr' ? '60 dana' : '60 days' },
              { days: 90, tokens: 10, label: language === 'sr' ? '90 dana' : '90 days' },
            ]
          : [
              { days: 7, tokens: 2, label: language === 'sr' ? '7 dana' : '7 days' },
              { days: 15, tokens: 3, label: language === 'sr' ? '15 dana' : '15 days' },
              { days: 30, tokens: 5, label: language === 'sr' ? '30 dana' : '30 days' },
            ];
        setDurationOptions(fallback);
        setSelectedDuration(fallback[0].days);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadDurationOptions();
  }, [listing.adKind, language]);

  const selectedOption = durationOptions.find(d => d.days === selectedDuration);
  const canAfford = selectedOption ? tokenBalance >= selectedOption.tokens : false;

  const handleConfirm = async () => {
    if (!canAfford) return;
    setProcessing(true);
    onConfirm(selectedDuration);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {listing.status === 'draft' 
              ? (language === 'sr' ? 'Aktiviraj oglas' : 'Activate Listing')
              : (language === 'sr' ? 'Reaktiviraj oglas' : 'Reactivate Listing')}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full">
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Listing info */}
          <div className="flex gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg mb-4">
            {listing.images[0] && (
              <img src={listing.images[0]} alt="" className="w-16 h-16 object-cover rounded-lg" />
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-slate-900 dark:text-white truncate">{listing.title}</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">{listing.location.city}</p>
            </div>
          </div>

          {/* Token balance */}
          <div className="flex items-center justify-between mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {language === 'sr' ? 'Vaš balans tokena:' : 'Your token balance:'}
            </span>
            <span className="font-bold text-amber-600">{tokenBalance} {language === 'sr' ? 'tokena' : 'tokens'}</span>
          </div>

          {/* Duration selection */}
          <div className="space-y-2 mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {language === 'sr' ? 'Trajanje oglasa' : 'Listing Duration'}
            </label>
            {loadingOptions ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-amber-500 border-t-transparent"></div>
              </div>
            ) : durationOptions.map(option => (
              <label
                key={option.days}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedDuration === option.days
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="duration"
                    checked={selectedDuration === option.days}
                    onChange={() => setSelectedDuration(option.days)}
                    className="text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-slate-900 dark:text-white">{option.label}</span>
                </div>
                <span className={`font-medium ${tokenBalance >= option.tokens ? 'text-amber-600' : 'text-red-500'}`}>
                  {option.tokens} {language === 'sr' ? 'tokena' : 'tokens'}
                </span>
              </label>
            ))}
          </div>

          {!canAfford && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
              <p className="text-sm text-red-600 dark:text-red-400">
                {language === 'sr' 
                  ? 'Nemate dovoljno tokena. Kupite tokene da biste reaktivirali oglas.'
                  : 'Insufficient tokens. Purchase tokens to reactivate.'}
              </p>
              <Link to="/tokeni" className="text-sm text-red-600 dark:text-red-400 underline font-medium">
                {language === 'sr' ? 'Kupi tokene' : 'Buy Tokens'}
              </Link>
            </div>
          )}

          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {language === 'sr' 
              ? 'Reaktivacija oglasa zahteva odobrenje administratora.'
              : 'Reactivation requires admin approval.'}
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canAfford || processing}
              className="flex-1 px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing 
                ? (language === 'sr' ? 'Obrada...' : 'Processing...')
                : (listing.status === 'draft' 
                    ? (language === 'sr' ? 'Aktiviraj' : 'Activate')
                    : (language === 'sr' ? 'Reaktiviraj' : 'Reactivate'))}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function MyListingsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isChecking, user, deleteProperty, updateProperty, tokenBalance } = useAppStore();
  const { language } = useI18n();
  const toast = useToast();
  const [editingListing, setEditingListing] = useState<Property | Auction | null>(null);
  const [reactivatingListing, setReactivatingListing] = useState<Property | Auction | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [myListings, setMyListings] = useState<(Property | Auction)[]>([]);
  const [loading, setLoading] = useState(true);

  // Wait for auth check to complete before redirecting
  useEffect(() => {
    if (!isChecking && !isAuthenticated) {
      navigate('/prijava');
    }
  }, [isChecking, isAuthenticated, navigate]);

  // Učitaj korisnikove oglase iz API-ja (uključujući pending)
  useEffect(() => {
    const loadMyListings = async () => {
      if (!isAuthenticated || isChecking) return;
      
      setLoading(true);
      try {
        const data = await getMyListings();
        
        // Transformiši podatke u frontend format
        // Napomena: getMyListings() već vraća transformisane podatke sa camelCase ključevima
        const transformed = data.map((p: any): Property => ({
          id: String(p.id),
          title: p.title || '',
          description: p.description || '',
          price: Number(p.price) || 0,
          currency: p.currency || 'EUR',
          adKind: p.adKind || p.ad_kind || 'Ponuda',
          adType: p.adType || p.ad_type || 'Prodaja',
          category: p.category || 'Stan',
          subcategory: p.subcategory || '',
          squareMeters: Number(p.squareMeters || p.square_meters) || 0,
          rooms: Number(p.rooms) || 0,
          floor: p.floor ? Number(p.floor) : undefined,
          totalFloors: p.totalFloors || p.total_floors ? Number(p.totalFloors || p.total_floors) : undefined,
          heating: p.heating || p.heatingType || p.heating_type || undefined,
          location: {
            region: p.region || '',
            city: p.city || '',
            address: p.address || '',
            lat: p.lat ? Number(p.lat) : undefined,
            lng: p.lng ? Number(p.lng) : undefined,
          },
          images: typeof p.images === 'string' ? JSON.parse(p.images || '[]') : (p.images || []),
          features: typeof p.features === 'string' ? JSON.parse(p.features || '{}') : (p.features || {}),
          views: Number(p.views) || 0,
          contactViews: Number(p.contact_views) || 0,
          status: p.status || 'pending',
          approved: Boolean(p.approved),
          featured: Boolean(p.featured),
          createdAt: p.createdAt || p.created_at || new Date().toISOString(),
          expiresAt: p.expiresAt || p.expires_at,
          userId: String(p.userId || p.user_id || ''),
          contactPhone: p.contactPhone || p.contact_phone || '',
          contactEmail: p.contactEmail || p.contact_email || '',
          // Potražnja specific fields
          priceMin: p.priceMin ? Number(p.priceMin) : undefined,
          priceMax: p.priceMax ? Number(p.priceMax) : undefined,
          squareMetersMin: p.squareMetersMin ? Number(p.squareMetersMin) : undefined,
          squareMetersMax: p.squareMetersMax ? Number(p.squareMetersMax) : undefined,
          roomsMin: p.roomsMin || undefined,
          roomsMax: p.roomsMax || undefined,
        }));
        
        setMyListings(transformed);
      } catch (err) {
        console.error('Error loading my listings:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadMyListings();
  }, [isAuthenticated, isChecking]);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const allMyListings = myListings;

  // Pagination logic
  const totalPages = Math.ceil(allMyListings.length / itemsPerPage);
  const paginatedListings = allMyListings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (item: typeof allMyListings[0]) => {
    if (item.status === 'draft') return {
      text: language === 'sr' ? 'U pripremi' : 'Draft',
      color: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
    };
    if (item.status === 'suspended') return {
      text: language === 'sr' ? 'Pauziran' : 'Paused',
      color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
    };
    if (!item.approved) return { 
      text: language === 'sr' ? 'Na čekanju' : 'Pending', 
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' 
    };
    if (item.status === 'expired') return { 
      text: language === 'sr' ? 'Istekao' : 'Expired', 
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
    };
    return { 
      text: language === 'sr' ? 'Aktivan' : 'Active', 
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
    };
  };

  const handleSaveEdit = (updates: Partial<Property>) => {
    if (editingListing) {
      updateProperty(editingListing.id, updates);
    }
  };

  // Funkcija za ponovni load oglasa
  const reloadMyListings = async () => {
    try {
      const data = await getMyListings();
      const transformed = data.map((p: any): Property => ({
        id: String(p.id),
        title: p.title || '',
        description: p.description || '',
        price: Number(p.price) || 0,
        currency: p.currency || 'EUR',
        adKind: p.ad_kind || 'Ponuda',
        adType: p.ad_type || 'Prodaja',
        category: p.category || 'Stan',
        subcategory: p.subcategory || '',
        squareMeters: Number(p.square_meters) || 0,
        rooms: Number(p.rooms) || 0,
        floor: p.floor ? Number(p.floor) : undefined,
        totalFloors: p.total_floors ? Number(p.total_floors) : undefined,
        heating: p.heating || p.heating_type || undefined,
        location: {
          region: p.region || '',
          city: p.city || '',
          address: p.address || '',
          lat: p.lat ? Number(p.lat) : undefined,
          lng: p.lng ? Number(p.lng) : undefined,
        },
        images: typeof p.images === 'string' ? JSON.parse(p.images || '[]') : (p.images || []),
        features: p.features || {},
        views: Number(p.views) || 0,
        contactViews: Number(p.contact_views) || 0,
        status: p.status || 'pending',
        approved: Boolean(p.approved),
        featured: Boolean(p.featured),
        createdAt: p.created_at || new Date().toISOString(),
        expiresAt: p.expires_at,
        userId: String(p.user_id || ''),
        contactPhone: p.contact_phone || '',
        contactEmail: p.contact_email || '',
      }));
      setMyListings(transformed);
    } catch (err) {
      console.error('Error reloading listings:', err);
    }
  };

  const handleReactivate = async (days: number) => {
    if (!reactivatingListing) return;
    
    try {
      // Use different endpoint for draft vs expired
      const endpoint = reactivatingListing.status === 'draft' 
        ? `/properties/${reactivatingListing.id}/activate`
        : `/properties/${reactivatingListing.id}/reactivate`;
      
      const res = await client.api.fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days })
      });
      
      const data = await res.json();
      if (data.success) {
        const title = reactivatingListing.status === 'draft'
          ? (language === 'sr' ? 'Oglas aktiviran' : 'Listing activated')
          : (language === 'sr' ? 'Zahtev poslat' : 'Request sent');
        const message = reactivatingListing.status === 'draft'
          ? (language === 'sr' 
            ? 'Oglas je aktiviran i čeka odobrenje administratora.' 
            : 'Listing activated. Awaiting admin approval.')
          : (language === 'sr' 
            ? 'Zahtev za reaktivaciju je poslat. Čeka odobrenje administratora.' 
            : 'Reactivation request sent. Awaiting admin approval.');
        toast.success(title, message);
        // Reload my listings from API
        await reloadMyListings();
      } else {
        toast.error(
          language === 'sr' ? 'Greška' : 'Error',
          data.error || (language === 'sr' ? 'Greška pri aktivaciji' : 'Activation error')
        );
      }
    } catch (err) {
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Greška pri slanju zahteva' : 'Error sending request'
      );
    }
    
    setReactivatingListing(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-4 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>{language === 'sr' ? 'Nazad' : 'Back'}</span>
          </button>
          
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Moji oglasi' : 'My Listings'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'sr' ? 'Upravljajte vašim oglasima' : 'Manage your listings'}
              </p>
            </div>
            <Link
              to="/novi-oglas"
              className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
            >
              + {language === 'sr' ? 'Novi oglas' : 'New Listing'}
            </Link>
          </div>

          {loading ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'sr' ? 'Učitavanje oglasa...' : 'Loading listings...'}
              </p>
            </div>
          ) : allMyListings.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Nemate oglasa' : 'No Listings'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {language === 'sr' 
                  ? 'Objavite svoj prvi oglas i pronađite kupce za vašu nekretninu.'
                  : 'Publish your first listing and find buyers for your property.'}
              </p>
              <Link
                to="/novi-oglas"
                className="inline-block px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Objavi oglas' : 'Create Listing'}
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedListings.map((listing) => {
                const status = getStatusBadge(listing);
                const expiresAt = listing.expiresAt ? new Date(listing.expiresAt) : null;
                const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
                const isExpired = listing.status === 'expired' || (daysLeft !== null && daysLeft <= 0);

                return (
                  <div key={listing.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden">
                    <div className="flex">
                      {listing.images[0] && (
                        <div className="w-40 h-32 flex-shrink-0">
                          <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900 dark:text-white">{listing.title}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                {status.text}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {listing.adKind} • {listing.adType} • {listing.category}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              {listing.location.region}, {listing.location.city}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-amber-600">
                              {listing.adKind === 'Potražnja' && (listing.priceMin || listing.priceMax) ? (
                                // Za Potražnja prikazujemo raspon cena
                                <>
                                  {listing.priceMin ? listing.priceMin.toLocaleString() : '0'}
                                  {' - '}
                                  {listing.priceMax ? listing.priceMax.toLocaleString() : '∞'}
                                  {' '}{listing.currency}
                                </>
                              ) : (
                                // Za Ponuda prikazujemo običnu cenu
                                <>
                                  {listing.price.toLocaleString()} {listing.currency}
                                </>
                              )}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {listing.views} {language === 'sr' ? 'pregleda' : 'views'}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {daysLeft !== null && daysLeft > 0 ? (
                              <span className={daysLeft <= 3 ? 'text-red-500' : ''}>
                                {language === 'sr' ? 'Ističe za' : 'Expires in'} {daysLeft} {
                                  language === 'sr' 
                                    ? (daysLeft === 1 ? 'dan' : 'dana')
                                    : (daysLeft === 1 ? 'day' : 'days')
                                }
                              </span>
                            ) : isExpired ? (
                              <span className="text-red-500">{language === 'sr' ? 'Istekao' : 'Expired'}</span>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Link
                              to={`/${listing.adKind === 'Aukcija' ? 'aukcije' : listing.adKind === 'Potražnja' ? 'potraznja' : 'ponuda'}/${listing.id}`}
                              className="px-3 py-1 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                            >
                              {language === 'sr' ? 'Pogledaj' : 'View'}
                            </Link>
                            <Link
                              to={`/izmeni-oglas/${listing.id}`}
                              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-500"
                            >
                              {language === 'sr' ? 'Izmeni' : 'Edit'}
                            </Link>
                            {/* Pause button for active listings */}
                            {listing.status === 'active' && (
                              <button
                                onClick={() => {
                                  toast.showConfirm({
                                    title: language === 'sr' ? 'Pauziraj oglas?' : 'Pause listing?',
                                    message: language === 'sr' ? 'Trajanje oglasa se nastavlja i dok je pauziran. Oglas neće biti vidljiv na sajtu.' : 'The listing duration continues even while paused. The listing will not be visible on the site.',
                                    type: 'warning',
                                    confirmText: language === 'sr' ? 'Pauziraj' : 'Pause',
                                    cancelText: language === 'sr' ? 'Otkaži' : 'Cancel',
                                    onConfirm: async () => {
                                      try {
                                        const res = await client.api.fetch(`/properties/${listing.id}/pause`, { method: 'POST' });
                                        if (res.ok) {
                                          toast.success(
                                            language === 'sr' ? 'Oglas pauziran' : 'Listing paused',
                                            language === 'sr' ? 'Oglas je uspešno pauziran.' : 'Listing has been paused successfully.'
                                          );
                                          loadListings();
                                        } else {
                                          const err = await res.json().catch(() => ({}));
                                          toast.error(
                                            language === 'sr' ? 'Greška' : 'Error',
                                            err.error || (language === 'sr' ? 'Greška pri pauziranju oglasa' : 'Error pausing listing')
                                          );
                                        }
                                      } catch (e) {
                                        toast.error(
                                          language === 'sr' ? 'Greška' : 'Error',
                                          language === 'sr' ? 'Greška pri pauziranju oglasa' : 'Error pausing listing'
                                        );
                                      }
                                    }
                                  });
                                }}
                                className="px-3 py-1 text-sm text-amber-600 hover:text-amber-500"
                              >
                                {language === 'sr' ? 'Pauziraj' : 'Pause'}
                              </button>
                            )}
                            {/* Resume button for suspended listings */}
                            {listing.status === 'suspended' && (
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await client.api.fetch(`/properties/${listing.id}/resume`, { method: 'POST' });
                                    if (res.ok) {
                                      toast.success(
                                        language === 'sr' ? 'Oglas aktiviran' : 'Listing resumed',
                                        language === 'sr' ? 'Oglas je ponovo aktivan.' : 'Listing is active again.'
                                      );
                                      loadListings();
                                    } else {
                                      const err = await res.json().catch(() => ({}));
                                      toast.error(
                                        language === 'sr' ? 'Greška' : 'Error',
                                        err.error || (language === 'sr' ? 'Greška pri nastavljanju oglasa' : 'Error resuming listing')
                                      );
                                    }
                                  } catch (e) {
                                    toast.error(
                                      language === 'sr' ? 'Greška' : 'Error',
                                      language === 'sr' ? 'Greška pri nastavljanju oglasa' : 'Error resuming listing'
                                    );
                                  }
                                }}
                                className="px-3 py-1 text-sm text-emerald-600 hover:text-emerald-500"
                              >
                                {language === 'sr' ? 'Nastavi' : 'Resume'}
                              </button>
                            )}
                            {isExpired && (
                              <button
                                onClick={() => setReactivatingListing(listing)}
                                className="px-3 py-1 text-sm text-emerald-600 hover:text-emerald-500"
                              >
                                {language === 'sr' ? 'Reaktiviraj' : 'Reactivate'}
                              </button>
                            )}
                            {listing.status === 'draft' && (
                              <button
                                onClick={() => setReactivatingListing(listing)}
                                className="px-3 py-1 text-sm text-emerald-600 hover:text-emerald-500"
                              >
                                {language === 'sr' ? 'Aktiviraj' : 'Activate'}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                toast.showConfirm({
                                  title: language === 'sr' ? 'Obriši oglas?' : 'Delete listing?',
                                  message: language === 'sr' ? 'Ova akcija je nepovratna. Oglas će biti trajno obrisan.' : 'This action cannot be undone. The listing will be permanently deleted.',
                                  type: 'danger',
                                  confirmText: language === 'sr' ? 'Obriši' : 'Delete',
                                  cancelText: language === 'sr' ? 'Otkaži' : 'Cancel',
                                  onConfirm: async () => {
                                    const success = await deleteProperty(listing.id);
                                    if (success) {
                                      toast.success(
                                        language === 'sr' ? 'Oglas obrisan' : 'Listing deleted',
                                        language === 'sr' ? 'Oglas je uspešno obrisan.' : 'Listing has been deleted successfully.'
                                      );
                                      loadListings();
                                    } else {
                                      toast.error(
                                        language === 'sr' ? 'Greška' : 'Error',
                                        language === 'sr' ? 'Greška pri brisanju oglasa' : 'Error deleting listing'
                                      );
                                    }
                                  }
                                });
                              }}
                              className="px-3 py-1 text-sm text-red-600 hover:text-red-500"
                            >
                              {language === 'sr' ? 'Obriši' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                totalItems={allMyListings.length}
              />
            </div>
          )}
        </motion.div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingListing && (
          <EditModal
            listing={editingListing}
            onClose={() => setEditingListing(null)}
            onSave={handleSaveEdit}
            language={language}
          />
        )}
      </AnimatePresence>

      {/* Reactivate Modal */}
      <AnimatePresence>
        {reactivatingListing && (
          <ReactivateModal
            listing={reactivatingListing}
            onClose={() => setReactivatingListing(null)}
            onConfirm={handleReactivate}
            tokenBalance={tokenBalance}
            language={language}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
